import Popup from "../../lib/Popup";

import * as PIXI from "pixi.js";
import gsap from "gsap";
import { IGameSnacks } from "../../lib/GameSnacks";
import ServiceLocator from "../../ServiceLocator";

import Button from "../../lib/Button";
import Session, { game_type } from "../../Session";
import { Signal } from "signals";
import PRNG from "../../lib/PRNG";
import PlayerSlot from "./PlayerSlot";
import Settings from "../Settings";
import TutorialPopup from "./TutorialPopup";

declare global {
    interface Window { GameSnacks: IGameSnacks; }
}


export default class OfflineRoomPopup extends Popup
{
    // #region Attributes //

    // private:

    private _gameType: game_type = null;

    private _classicModeBtn: Button = null;
    private _pointsModeBtn: Button = null;
    private _gameStartedBg: PIXI.Sprite = null;
    private _versus: PIXI.Sprite = null;
    private _versusExplosion: PIXI.Sprite = null;
    private _headerText: PIXI.Text = null;
    private _footerText: PIXI.Text = null;
    private _arrPlayerSlot: Array<PlayerSlot> = null;
    private _settings: Settings = null;
    private _backBtn: Button = null;
    private _startBtn: Button = null;
    
    private _gameReadyTl: gsap.core.Timeline = null;

    private _onGameStarted: Signal = null;

    // #endregion //


    public set gameType( value: game_type ) { this._gameType = value; }

    public get onGameStarted(): Signal { return this._onGameStarted; } 


    // #region Methods //

    // public:

    public onEnter(): void
    {
        super.onEnter();        

        this._onGameStarted = new Signal();

        this._arrPlayerSlot = [];

        if ( this._gameType === null )
        {
            this._gameType = game_type.CLASSIC_OFFLINE;
        }

        let bg: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.matchmaking_background_01.texture );
        bg.anchor.set( 0.5 );
        bg.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._root.addChild( bg );

        this._gameStartedBg = new PIXI.Sprite( ServiceLocator.resources.matchmaking_background_02.texture );
        this._gameStartedBg.visible = false;
        this._gameStartedBg.anchor.set( 0.5 );
        this._gameStartedBg.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._root.addChild( this._gameStartedBg );

        this._versusExplosion = new PIXI.Sprite( ServiceLocator.resources.vs_background_effect.texture );
        this._versusExplosion.visible = false;
        this._versusExplosion.anchor.set( 0.5 );
        this._versusExplosion.position.set( ServiceLocator.app.width * 0.5, 619 );
        this._root.addChild( this._versusExplosion );

        this._headerText = new PIXI.Text( "Play Offline", { fontFamily: "jaro_regular", fontSize: 80, fill: 0xffffff, stroke: 0x000000, strokeThickness: 12, letterSpacing: 2 } );
        this._headerText.anchor.set( 0.5 );
        this._headerText.position.set( ServiceLocator.app.width * 0.5, 158 );
        this._root.addChild( this._headerText );

        let gameModeText: PIXI.Text = new PIXI.Text( "Choose game mode:", { fontFamily: "jaro_regular", fontSize: 40, fill: 0xffffff } );
        gameModeText.anchor.set( 0.5 );
        gameModeText.position.set( ServiceLocator.app.width * 0.5, 270 );
        this._root.addChild( gameModeText );

        const kArrPos: Array<PIXI.Point> = [ 
            new PIXI.Point( 158, 614 ),
            new PIXI.Point( ServiceLocator.app.width - 158, 614 ) ];
        for ( let i: number = 0; i < Session.i.kGameRoomSize; ++i )
        {
            let playerSlot: PlayerSlot = new PlayerSlot( ServiceLocator.resources.matchmaking_avatar_base.texture );
            playerSlot.position.copyFrom( kArrPos[ i ] );
            this._root.addChild( playerSlot );            
            //
            this._arrPlayerSlot.push( playerSlot );
        }

        this._versus = new PIXI.Sprite( ServiceLocator.resources.vs.texture );
        this._versus.visible = false;
        this._versus.anchor.set( 0.5 );
        this._versus.position.set( ServiceLocator.app.width * 0.5 + 8, 605 );
        this._root.addChild( this._versus );

        this._backBtn = new Button( { on: "button_back", pressed: "button_back_pressed" } );
        this._backBtn.anchor.set( 0.5 );
        this._backBtn.position.set( 48, 44 + 16 );
        this._root.addChild( this._backBtn );
        this._backBtn.on( "pointertap", () => { this._onClosed.dispatch( this ); } );

        this._footerText = new PIXI.Text( "Take turns playing on the same device", { fontFamily: "jaro_regular", fontSize: 40, fill: 0xffffff } );
        this._footerText.anchor.set( 0.5 );
        this._footerText.position.set( ServiceLocator.app.width * 0.5, 900 );
        this._root.addChild( this._footerText );

        this._settings = new Settings();
        this._settings.init( true );
        this._settings.position.set( 592, 7 + 16 );
        this._root.addChild( this._settings );
        this._settings.onTutorial.add( () => { 
            let tutorialPopup: TutorialPopup = new TutorialPopup();
            tutorialPopup.init();
            this._root.addChild( tutorialPopup );
            tutorialPopup.onClosed.add( ( popup: TutorialPopup ) => {
                popup.end();
            } );
        } );
        this._settings.onHome.addOnce( () => { 
            this._onClosed.dispatch( this );
        } );

        this._classicModeBtn = new Button( { on: "button_mode_classic", pressed: "button_mode_classic_pressed" } );
        this._classicModeBtn.interactive = false;
        this._classicModeBtn.anchor.set( 0.5 );
        this._classicModeBtn.position.set( 217, 371 );
        this._root.addChild( this._classicModeBtn );
        this._classicModeBtn.on( "pointertap", this._onClassicModeBtn_Click, this );

        this._pointsModeBtn = new Button( { on: "button_mode_301_off", pressed: "button_mode_301_pressed" } );
        this._pointsModeBtn.anchor.set( 0.5 );
        this._pointsModeBtn.position.set( 421, 371 );
        this._root.addChild( this._pointsModeBtn );
        this._pointsModeBtn.on( "pointertap", this._onPointsModeBtn_Click, this );

        Session.i.room = [
            { 
                username: { prefix: "P1", suffix: "" },
                avatar: { src: "avatar_you", alt: null },
                uid: "fake",
                isFake: true 
            },
            {
                username: { prefix: "P2", suffix: "" },
                avatar: { src: "avatar_you", alt: null },
                uid: "fake",
                isFake: true 
            } ];
        this._arrPlayerSlot[ 0 ].setPlayer( Session.i.room[ 0 ] );
        this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 1 ] );

        this._startBtn = new Button( { on: "button_play", pressed: "button_play_pressed" } );
        this._startBtn.anchor.set( 0.5 );
        this._startBtn.position.set( ServiceLocator.app.width * 0.5, 996 + 44 );
        this._root.addChild( this._startBtn );
        this._startBtn.on( "pointertap", this._onStartBtn_Click, this );
    }

        private _onClassicModeBtn_Click(): void
        {
            this._gameType = game_type.CLASSIC_OFFLINE;

            this._classicModeBtn.interactive = false;
            this._pointsModeBtn.interactive = true;

            this._classicModeBtn.setTextures( { on: "button_mode_classic", pressed: "button_mode_classic_pressed" } );
            this._pointsModeBtn.setTextures( { on: "button_mode_301_off", pressed: "button_mode_301_pressed" } );
        }

        private _onPointsModeBtn_Click(): void
        {
            this._gameType = game_type.POINTS_OFFLINE;

            this._classicModeBtn.interactive = true;
            this._pointsModeBtn.interactive = false;

            this._classicModeBtn.setTextures( { on: "button_mode_classic_off", pressed: "button_mode_classic_pressed" } );
            this._pointsModeBtn.setTextures( { on: "button_mode_301", pressed: "button_mode_301_pressed" } );
        }

        private _onStartBtn_Click(): void
        {/*
            this._innerLoadingCircle.visible = false;
            this._outerLoadingCircle.visible = false;*/
            this._startBtn.visible = false;
            //
            this._backBtn.interactive = false;
            gsap.to( this._backBtn, { alpha: 0, duration: 0.4, ease: "linear.inOut" } );
            //
            this._settings.interactive = false;
            gsap.to( this._settings, { alpha: 0, duration: 0.4, ease: "linear.inOut" } );
            //
            this._footerText.text = "All ready! Let's Play!"

            Session.i.prng = new PRNG( Math.floor( Math.random() * 9999 ) );
            Session.i.profile = Session.i.room[ 0 ];

            this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 1 ] );

            Session.i.room[ 0 ].dartSkinIndex = ServiceLocator.saved.data.selDartIndex;
            Session.i.room[ 1 ].dartSkinIndex = Session.i.room[ 0 ].dartSkinIndex;

            this._gameStartedBg.visible = true;
            this._gameStartedBg.alpha = 0;
            //
            this._versusExplosion.visible = true;
            this._versusExplosion.scale.set( 1, 0 );
            //
            this._versus.visible = true;
            this._versus.scale.set( 0 );
            //
            this._gameReadyTl = new gsap.core.Timeline();
            this._gameReadyTl.to( this._gameStartedBg, { alpha: 1, duration: 1.6, ease: "sine.out" }, 0 );
            this._gameReadyTl.to( this._versusExplosion.scale, { y: 1, duration: 0.8, ease: "expo.out" }, 0 );
            this._gameReadyTl.to( this._versus.scale, { x: 1, y: 1, duration: 1, ease: "back.out" }, 0 );
            this._gameReadyTl.call( () => { this._onGameStarted.dispatch( this, this._gameType ); }, null, ">+2" );
        }

    public onLeave(): void
    {
        this._onGameStarted.dispose();
        this._onGameStarted = null;

        if ( this._gameReadyTl )
        {
            this._gameReadyTl.kill();
            this._gameReadyTl = null;
        }

        this._settings.end();
        this._settings = null;

        super.onLeave();
    }

    // #endregion //
}